import React, { useCallback, useEffect, useState } from "react";
import { makeStyles, withStyles } from "@material-ui/core/styles";
import { clone, equals, head, pickBy, pluck } from "ramda";
import { anyTrue, isBlank, notBlank } from "../../../util/ext";
import { useIsAdmin, useIsCreatorManager } from "../../../auth/authUtils";

import Container from "@material-ui/core/Container";
import Grid from "@material-ui/core/Grid";

import SnackAlert from "../../../components/SnackAlert";
import { useSnackAlert } from "../../../common/use-snackAlert";
import ConfirmDialog from "../../../components/ConfirmDialog";
import { useConfirm } from "../../../common/use-confirm";
import {
  useAnthology,
  useAnthologyUpdate,
  useUploadAnthologyOrganizationLogo,
  useUploadAnthologyThumbnail,
} from "../anthologyServices";
import { useProjectLink } from "../../creatorServices";

import {
  validateAnthology,
  validateAnthologyField,
  hasError,
  firstError,
} from "../anthologyValidation";
import { MediaContext } from "../../common/media-context";
import ProjectLink from "../../common/ProjectLink";
import Divider from "@material-ui/core/Divider";
import EditAnthologyInfoColumn from "./EditAnthologyInfoColumn";
import EditCategoriesColumn from "../../common/EditCategoriesColumn";
import EditDescriptionColumn from "../../common/EditDescriptionColumn";
import EditThumbnailOverlay from "../../common/EditThumbnailOverlay";
import PanelLoadingIndicator from "../../../components/PanelLoadingIndicator";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import { Dialog, DialogContent } from "@material-ui/core";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import EditOrganizationLogoOverlay from "./EditOrganizationLogoOverlay";
import Skeleton from "@material-ui/lab/Skeleton";
import EditContentGroupStatus from "../../common/EditContentGroupStatus";
import SaveButton from "../../../components/SaveButton";

const styles = theme => ({
  root: {
    padding: "40px 0 0 0",
    flexGrow: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    flexWrap: "wrap",
  },

  dialogRoot: {
    padding: "0 50px",
  },

  header: {
    margin: 0,
    padding: "40px 0 20px 0",
  },

  closeButton: {
    color: theme.palette.primary.darker,
    fontWeight: "bolder",
  },

  closeButtonIcon: {
    fontSize: "3.2rem",
  },

  loadingContainer: {
    padding: theme.spacing(2, 5),
    height: "calc(100vh - 200px)",
  },

  scrollArea: {
    height: "calc(100vh - 300px)",
    overflowY: "auto",
  },

  editPanel: {
    height: "100%",
    width: "calc(100% - 15px)",
  },

  actions: {
    paddingTop: 20,
    paddingBottom: 70,
  },
});

const useStyles = makeStyles(styles);

const DialogTitle = withStyles(styles)(props => {
  const { children, classes, onClose, anthology, ...other } = props;
  const isAdmin = useIsAdmin();
  return (
    <MuiDialogTitle disableTypography className={classes.header} {...other}>
      <Grid container justify="space-between" alignItems="center">
        <Grid item xs>
          <Typography variant="h6">{children}</Typography>
        </Grid>

        <Grid container item alignItems="center" justify="flex-end" xs>
          {isAdmin && (
            <Grid item xs={3}>
              <EditContentGroupStatus />
            </Grid>
          )}

          <Grid item>
            {onClose ? (
              <IconButton
                aria-label="close"
                className={classes.closeButton}
                onClick={onClose}
              >
                <CloseIcon classes={{ root: classes.closeButtonIcon }} />
              </IconButton>
            ) : null}
          </Grid>
        </Grid>
      </Grid>
    </MuiDialogTitle>
  );
});

const objectDiff = media => (val, key) => !equals(media[key], val);

const validator = {
  validateField: validateAnthologyField,
  hasError: hasError,
  firstError: firstError,
};

const AnthologyEditDialog = props => {
  const { visible, id, onCancel } = props;
  const classes = useStyles();
  const isAdmin = useIsAdmin();
  const isCreatorManager = useIsCreatorManager();

  //-- Manage the alert message
  // const { SnackAlert, showAlert } = useSnackAlert();
  const snackAlertHandler = useSnackAlert();
  const { showAlert } = snackAlertHandler;

  //-- Manage the confirm dialog
  const confirmHandler = useConfirm();
  const { showConfirm } = confirmHandler;

  //-- Set up the thumbnail uploader
  const thumbnailUpload = useUploadAnthologyThumbnail();

  //-- Set upload organization logo uploader
  const organizationLogoUpload = useUploadAnthologyOrganizationLogo();

  //-- Set up overall state
  const [anthology, setAnthology] = useState({});
  const [initialAnthology, setInitialAnthology] = useState({});
  const [thumbnailUrl, setThumbnailUrl] = useState(null);
  const [orgLogoUrl, setOrgLogoUrl] = useState(null);
  const [errors, setErrors] = useState({});

  const result = useAnthology(id);
  const projectLinkResults = useProjectLink(id);
  useEffect(() => {
    console.log("Checking for success...");
    if (result.isSuccess) {
      console.log("Got the anthology...");
      setAnthology(result.data);
      setInitialAnthology(clone(result.data));

      setOrgLogoUrl(result.data?.organizationLogoUrl ?? null);
    }
  }, [result.isSuccess, result.data]);

  //-- Handle saving the content
  const anthologyUpdate = useAnthologyUpdate();
  const [saveDisabled, setSaveDisabled] = useState(true);
  const handleSave = useCallback(async () => {
    setSaveDisabled(true);
    const validation = validateAnthology(anthology);

    try {
      if (notBlank(validation)) {
        setErrors(validation);
        throw new Error("Please address missing or invalid values");
      }
      const update = pickBy(objectDiff(initialAnthology), anthology);

      await anthologyUpdate.mutateAsync({
        data: update,
        resourceIds: anthology,
      });

      setInitialAnthology(anthology);

      showAlert("success", ["Anthology Saved"]);
    } catch (err) {
      const messages = err?.body?.errors
        ? pluck("message", err.body.errors)
        : [err.message];
      showAlert("error", messages);
    }
  }, [initialAnthology, anthology, anthologyUpdate, showAlert]);

  const handleCloseClicked = useCallback(() => {
    const validation = validateAnthology(anthology);

    try {
      if (notBlank(validation)) {
        setErrors(validation);
        throw new Error("Please address missing or invalid values");
      }
      const update = pickBy(objectDiff(initialAnthology), anthology);

      if (isBlank(update)) {
        onCancel();
      } else {
        showConfirm(
          "Discard Changes?",
          "You have made changes to this anthology without saving.  Do you wish to discard these changes and go back to the anthology list?",
          {
            onConfirm: () => {
              onCancel();
            },
          },
        );
      }
    } catch (err) {
      showAlert("error", [err.message]);
    }
  }, [anthology, initialAnthology, showAlert, showConfirm, onCancel]);

  useEffect(() => {
    const update = pickBy(objectDiff(initialAnthology), anthology);
    if (notBlank(update)) {
      setSaveDisabled(false);
    }
  }, [anthology, initialAnthology]);

  return (
    <Dialog
      fullScreen
      classes={{ paper: classes.dialogRoot }}
      open={visible}
      onClose={handleCloseClicked}
    >
      {notBlank(anthology) ? (
        <MediaContext.Provider
          value={{
            initial: initialAnthology,
            media: anthology,
            setMedia: setAnthology,
            errors: errors,
            setErrors: setErrors,
            thumbnailUrl: thumbnailUrl,
            setThumbnailUrl: setThumbnailUrl,
            orgLogoUrl: orgLogoUrl,
            setOrgLogoUrl: setOrgLogoUrl,
          }}
        >
          <DialogTitle
            id="edit-antholgy-dialog-title"
            onClose={handleCloseClicked}
          >
            Edit Anthology
          </DialogTitle>

          <Divider />

          <DialogContent id="anthology-edit" classes={{ root: classes.root }}>
            <SnackAlert handler={snackAlertHandler} />
            <ConfirmDialog handler={confirmHandler} />

            <Container
              id="anthology-edit-container"
              maxWidth={false}
              disableGutters
            >
              <div className={classes.scrollArea}>
                <Grid
                  id="anthology-edit-panel"
                  container
                  alignContent="flex-start"
                  className={classes.editPanel}
                >
                  <Grid item container>
                    <Grid item>
                      <EditThumbnailOverlay
                        accept="jpg, jpeg, image/jpeg, image/jpg, png, image/png"
                        showAlert={showAlert}
                        uploader={thumbnailUpload}
                      />
                    </Grid>

                    <Grid item xs>
                      <EditAnthologyInfoColumn />
                    </Grid>
                  </Grid>

                  <Grid item xs={12} style={{ paddingTop: 25, marginTop: 25 }}>
                    <EditDescriptionColumn
                      enableLinks
                      error={hasError(errors, "description")}
                      helperText={firstError(errors, "description")}
                      onBlur={() =>
                        setErrors(
                          validateAnthologyField(
                            "description",
                            anthology.description,
                          ),
                        )
                      }
                    />
                  </Grid>

                  <Grid item xs>
                    <EditCategoriesColumn
                      showFourCategories
                      validator={validator}
                    />
                  </Grid>

                  <Grid item xs="auto">
                    <EditOrganizationLogoOverlay
                      accept="png, image/png, jpg, jpeg, image/jpeg, image/jpg"
                      showAlert={showAlert}
                      uploader={organizationLogoUpload}
                    />
                  </Grid>

                  {anyTrue([isAdmin, isCreatorManager]) && (
                    <Grid item xs>
                      {projectLinkResults.isLoading ? (
                        <Skeleton />
                      ) : (
                        <ProjectLink
                          id={id}
                          showAlert={showAlert}
                          projectLink={head(projectLinkResults.data)}
                        />
                      )}
                    </Grid>
                  )}
                </Grid>
              </div>
            </Container>
          </DialogContent>

          <DialogActions classes={{ root: classes.actions }}>
            <Button onClick={handleCloseClicked}>Cancel</Button>
            <SaveButton
              loading={anthologyUpdate.isLoading}
              disabled={saveDisabled}
              onClick={handleSave}
            />
          </DialogActions>
        </MediaContext.Provider>
      ) : (
        <div className={classes.loadingContainer}>
          <PanelLoadingIndicator />
        </div>
      )}
    </Dialog>
  );
};

export default AnthologyEditDialog;
